import type { InterfaceOverrideEntity } from '@unifyapps/defs/types/interface/interfaceOverride';
import type {
  QueryClient,
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@unifyapps/network/react-query';
import { useQuery } from '@unifyapps/network/react-query';
import type { ErrorType } from '@unifyapps/network/utils/fetch';
import { executeFetch } from '@unifyapps/network/utils/fetch';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same as in the generated hooks
type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

export const getAuthDeployedInterfaceOverride = (
  options?: SecondParameter<typeof executeFetch>,
  signal?: AbortSignal,
) => {
  return executeFetch<InterfaceOverrideEntity>(
    {
      url: `/auth/interface/override/deployed`,
      method: 'GET',
      signal,
    },
    options,
  );
};

export const getAuthDeployedInterfaceOverrideQueryKey = () => {
  return [`/auth/interface/override/deployed`] as const;
};

export const getAuthDeployedInterfaceOverrideQueryOptions = <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>, TError, TData>
  >;
  request?: SecondParameter<typeof executeFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAuthDeployedInterfaceOverrideQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>> = ({
    signal,
    meta,
  }) =>
    getAuthDeployedInterfaceOverride(
      {
        ...requestOptions,
        meta,
      },
      signal,
    );

  return { queryKey, queryFn, staleTime: 5000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAuthDeployedInterfaceOverrideQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>
>;
export type GetAuthDeployedInterfaceOverrideQueryError = ErrorType<unknown>;

export const useGetAuthDeployedInterfaceOverride = <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>, TError, TData>
  >;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getAuthDeployedInterfaceOverrideQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const prefetchGetAuthDeployedInterfaceOverride = async <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterfaceOverride>>, TError, TData>
    >;
  },
): Promise<QueryClient> => {
  const queryOptions = getAuthDeployedInterfaceOverrideQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * Prefetches the deployed auth interface override and returns it straight from the cache
 * (undefined if the app has no override). Mirrors `fetchAuthDeployedInterfaceById` — the read sits
 * next to the prefetch and the client hook so all three key off the same
 * `/auth/interface/override/deployed` route. Callers wanting a degrade-on-failure policy should wrap
 * this in their own try/catch.
 */
export async function fetchAuthDeployedInterfaceOverride(
  queryClient: QueryClient,
): Promise<InterfaceOverrideEntity | undefined> {
  await prefetchGetAuthDeployedInterfaceOverride(queryClient);
  return queryClient.getQueryData<InterfaceOverrideEntity>(
    getAuthDeployedInterfaceOverrideQueryKey(),
  );
}
